import React from 'react';
import {
	Alert,
	Button,
	Chip,
	FormControl,
	Typography,
	Stack,
	DialogContent,
	DialogActions,
	FormHelperText,
} from '@mui/material';

import PreferredAssessmentDate from '../../../components/request/PreferredAssessmentDate';
import { Request } from '../../../models';
import Dialog from '../../../components/dialog';

import { fDateTime } from '../../../utils/formatTime';

import { type FormikProps, getIn } from 'formik';

interface PrefAssessmentDateValues {
	prefAssessmentDates: (Date | null)[];
}

interface PrefAssessmentDateStepProps {
	formik: FormikProps<PrefAssessmentDateValues>;
	prefAssessmentDateLabels: string[];
}

export const PreferredAssessmentDates: React.FC<PrefAssessmentDateStepProps> = ({ formik, prefAssessmentDateLabels }) => {
	const [open, setOpen] = React.useState(false);
	const [selectedDateIndex, setSelectedDateIndex] = React.useState<number | null>(null);
	const [localDate, setLocalDate] = React.useState<Date>(new Date());

	const handleCloseAssessmentDateDialog = () => {
		setOpen(false);
	};

	const handleConfirmAssessmentDateDialog = () => {
		if (selectedDateIndex !== null) {
			formik.setFieldValue(`prefAssessmentDates[${selectedDateIndex}]`, localDate);
		}
		setOpen(false);
	};

	const onDateTimeLabelClick = (e: React.MouseEvent<HTMLDivElement>) => {
		const dateTimeIndex = Number(e.currentTarget.getAttribute('data-pref-assessment-date-index'));
		setLocalDate(formik.values.prefAssessmentDates[dateTimeIndex] ?? Request.calculateNextValidDate(new Date()));
		setSelectedDateIndex(dateTimeIndex);
		setOpen(true);
	};

	const ifError = getIn(formik.errors, 'prefAssessmentDates');

	return (
		<>
			{/* error prop sets FormHelperText to the associated error color in palette */}
			<FormControl margin="normal" component="fieldset" variant="standard" error={Boolean(ifError)}>
				<Stack
					spacing={2}
					sx={{
						alignItems: 'flex-start',
					}}
				>
					<Alert severity="info">
						We'll try our best to schedule your preferred time. Your appointment is NOT confirmed until you receive a
						booking email from us. Appointments are typically completed within 3 business days.
					</Alert>
					{prefAssessmentDateLabels.map((label, index) => (
						<React.Fragment key={label}>
							<Typography variant="subtitle2" color="grey.500">
								{label}
							</Typography>
							<Chip
								label={
									formik.values.prefAssessmentDates[index]
										? fDateTime(formik.values.prefAssessmentDates[index])
										: 'Select a Date'
								}
								color="primary"
								variant="outlined"
								data-pref-assessment-date-index={index}
								onClick={onDateTimeLabelClick}
							/>
						</React.Fragment>
					))}
					{Boolean(ifError) && <FormHelperText>{ifError}</FormHelperText>}
				</Stack>
				<Dialog open={open} onClose={handleCloseAssessmentDateDialog}>
					<DialogContent>
						<PreferredAssessmentDate initialDate={localDate} onChange={setLocalDate} />
					</DialogContent>

					<DialogActions>
						<Stack direction="row" spacing={4} justifyContent="flex-end">
							<Button onClick={handleCloseAssessmentDateDialog}>Cancel</Button>
							<Button onClick={handleConfirmAssessmentDateDialog} variant="contained" color="primary">
								Confirm
							</Button>
						</Stack>
					</DialogActions>
				</Dialog>
			</FormControl>
		</>
	);
};
